import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "antd";
import { FileTextOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import localStorageService from "../../../services/localStorage.service";
import ReleaseService, { RELEASES_SEEN_EVENT } from "../../../services/release.service";
import "./styles.scss";

const MAX_ITEMS_PER_RELEASE = 3;
const GROUP_LABELS = { new: "New", improved: "Improved", fixed: "Fixed" };

// "While you were away": a summary for users who missed several releases (the backend decides who).
const CatchUpPanel = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [missedReleases, setMissedReleases] = useState([]);
  const [isAnnouncement, setIsAnnouncement] = useState(false);
  const [open, setOpen] = useState(false);
  const checked = useRef(false);

  // Check once per page load, only when logged in, and never on the Release Notes page itself.
  useEffect(() => {
    const loggedIn = localStorageService.getToken() !== null;
    // After a logout, check again for whoever logs in next in this tab.
    if (!loggedIn) checked.current = false;
    if (checked.current || !loggedIn || location.pathname === "/release_notes") return;
    checked.current = true;

    ReleaseService.getStatus()
      .then((status) => {
        setIsAnnouncement(Boolean(status.announcement));
        return status.show_catch_up ? ReleaseService.getReleases() : [];
      })
      .then((releases) => {
        const missed = releases.filter((release) => release.is_new);
        if (missed.length > 0) {
          setMissedReleases(missed);
          setOpen(true);
        }
      })
      .catch(() => {
        // Try again on the next page change.
        checked.current = false;
      });
  }, [location.pathname]);

  const close = useCallback(() => {
    setOpen(false);
    ReleaseService.markSeen().catch(() => {});
  }, []);

  // Hide the panel if the notes get marked as seen elsewhere, e.g. on the Release Notes page.
  useEffect(() => {
    const hide = () => setOpen(false);
    window.addEventListener(RELEASES_SEEN_EVENT, hide);
    return () => window.removeEventListener(RELEASES_SEEN_EVENT, hide);
  }, []);

  // The Esc key closes the panel, like other pop-ups.
  useEffect(() => {
    if (!open) return;
    const handleKey = (event) => {
      if (event.key === "Escape") close();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open, close]);

  const seeAll = () => {
    setOpen(false);
    navigate("/release_notes"); // the Release Notes page marks everything as seen
  };

  if (!open) return null;

  const count = missedReleases.length;

  return (
    <section
      className="catch-up"
      role="dialog"
      aria-modal="false"
      aria-labelledby="catch-up-title"
    >
      <div className="catch-up-header">
        <div className="catch-up-icon" aria-hidden="true">
          <FileTextOutlined />
        </div>
        <div>
          <h2 className="catch-up-title" id="catch-up-title">
            {/* Big announcements get their own title; otherwise it's the 4+ missed releases summary. */}
            {isAnnouncement ? "New in the portal" : "While you were away"}
          </h2>
          <p className="catch-up-sub">
            {count} new release{count === 1 ? "" : "s"} since your last visit
          </p>
        </div>
        <button
          type="button"
          className="catch-up-close"
          aria-label="Close"
          onClick={close}
        >
          ×
        </button>
      </div>

      <div className="catch-up-body">
        {missedReleases.map((release) => {
          const shownItems = release.items.slice(0, MAX_ITEMS_PER_RELEASE);
          const hiddenCount = release.items.length - shownItems.length;

          return (
            <div key={release.version} className="catch-up-release">
              <div className="catch-up-release-head">
                <span className="catch-up-version">{release.version}</span>
                <span className="catch-up-date">
                  {dayjs(release.release_date).format("D MMM YYYY")}
                </span>
              </div>

              {shownItems.map((item, index) => (
                <div key={index} className="catch-up-item">
                  <span className={`catch-up-chip ${item.group}`}>
                    {GROUP_LABELS[item.group]}
                  </span>
                  <span>{item.text}</span>
                </div>
              ))}

              {hiddenCount > 0 && (
                <div className="catch-up-more">
                  + {hiddenCount} more change{hiddenCount === 1 ? "" : "s"}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="catch-up-footer">
        <Button onClick={seeAll}>See all release notes</Button>
      </div>
    </section>
  );
};

export default CatchUpPanel;
